import { FC, useState } from "react";

import { Box, Stack } from "@mui/material";
import {
  EmailContentType,
  getFilenameFromUrl,
  Locales,
  LocaleContent,
  normalizeContentLocale,
  DonationFormCategory,
} from "@simplyk/common";
import { FieldErrors, FormProvider, useForm, useWatch } from "react-hook-form";

import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { AutomatedCommunicationCard } from "./components/AutomatedCommunicationCard";
import { CustomMessagesDisabledBanner } from "./components/CustomMessagesDisabledBanner";
import { SendAutomatedEmailTestButton } from "./components/SendAutomatedEmailTestButton";
import { TeamActivityNotificationsCard } from "./components/TeamActivityNotificationsCard";
import { useShouldShowCustomMessagesDisabledBanner } from "./helpers/useShouldShowCustomMessagesDisabledBanner";
import { useApplyThankYouAIDefaults } from "./hooks/useApplyThankYouAIDefaults";
import { AutomatedEmailOpenedCard } from "./types";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { Translate, useTranslate } from "@/common/hooks/useTranslate";
import { StepContainer } from "@/components/LiveEditorElements/StepContainer/StepContainer";
import { ExtraWysiwygVariable } from "@/components/OutlinedTextFieldWysiwyg/type";
import { EmailContentsLoadState } from "@/features/LiveFormEditor/common/EmailContentsLoadState";
import { FormEmailContents, getEditorEmailText } from "@/features/LiveFormEditor/common/formEmailContents";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useUpdateDonationDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateDonationDataInPreview";
import { FormStepContainer } from "@/features/LiveFormEditor/Steps/FormStepContainer";

type ThankYouAttachment = { fileUrl: string | null; filename: string | null };

export interface DonationAutomatedCommunicationsFormData {
  thankYou: { subject?: string; content: string; attachment?: ThankYouAttachment };
  fundraiserThankYou: { content: string };
  autoRenewal: { enabled: boolean; delayDays: number; subject: string; content: string };
  teamActivityNotificationsEnabled: boolean;
}

export const DonationLiveEditorStepAutomatedCommunications: FC = () => {
  const { t } = useTranslate();
  const {
    isStepChanging,
    apiFormObject: apiDonationForm,
    optionalFundraiserToken,
  } = useLiveFormEditorContext<"donation-form">();
  const emailContentsQuery = trpc.getDonationAutomatedEmailContents.useQuery({
    donationFormId: apiDonationForm.id,
    fundraiserToken: optionalFundraiserToken,
  });

  if (emailContentsQuery.data) {
    return <DonationAutomatedCommunicationsForm emailContents={emailContentsQuery.data} />;
  }

  const { stepTitle, stepDescription } = getStepWording(t);
  return (
    <StepContainer
      title={stepTitle}
      description={stepDescription}
      isStepChanging={isStepChanging}
      slots={{ actionFooter: null }}
    >
      <EmailContentsLoadState hasError={emailContentsQuery.isError} onRetry={() => void emailContentsQuery.refetch()} />
    </StepContainer>
  );
};

const DonationAutomatedCommunicationsForm: FC<{ emailContents: FormEmailContents }> = ({ emailContents }) => {
  const { t } = useTranslate();
  const { currentUser } = useCurrentUserContext();
  const {
    apiFormObject: apiDonationForm,
    showRenewalEmails,
    optionalFundraiserToken,
    formCategory,
    editorMode,
    liveFormData,
    setLiveFormData,
  } = useLiveFormEditorContext<"donation-form">();

  const showAutoRenewal = showRenewalEmails;
  const showFundraiserCard = formCategory === DonationFormCategory.PeerToPeerCampaign && editorMode === "org";
  const isPeerToPeerChildPage = Boolean(apiDonationForm.campaignId) && !apiDonationForm.isPrimaryCampaign;
  const showTeamActivityNotificationsCard = showFundraiserCard && !isPeerToPeerChildPage;
  const isCollapsible = showFundraiserCard || showAutoRenewal;
  const shouldShowCustomMessagesDisabledBanner = useShouldShowCustomMessagesDisabledBanner();

  const hasLocation = Boolean(apiDonationForm.contact?.address);

  const localizedField = apiDonationForm.donationFormFields?.find((field) => field.locale === apiDonationForm.locale);

  const orgName = apiDonationForm.organization?.name || "";
  const userName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(" ");
  const locale = (apiDonationForm.locale as Locales) || Locales.EN;
  const thankYouText = getEditorEmailText(emailContents, EmailContentType.THANK_YOU, locale);
  const autoRenewalText = getEditorEmailText(emailContents, EmailContentType.MEMBERSHIP_AUTO_RENEWAL, locale);

  const autoRenewalVariables: ExtraWysiwygVariable[] = [
    { tag: "{{Renewal amount}}", label: t("dashboard", "membership.renewalEmail.variable.renewalAmount") },
    { tag: "{{Payment method}}", label: t("dashboard", "membership.renewalEmail.variable.paymentMethod") },
    { tag: "{{Renewal date}}", label: t("dashboard", "ticketing.renewalDate") },
  ];

  const defaultValues: DonationAutomatedCommunicationsFormData = {
    thankYou: {
      subject: thankYouText.subject || "",
      content: thankYouText.body || "",
      attachment: {
        fileUrl: thankYouText.attachmentUrl || null,
        filename: getFilenameFromUrl(thankYouText.attachmentUrl) || null,
      },
    },
    fundraiserThankYou: { content: localizedField?.fundraiserEmailBody || "" },
    autoRenewal: {
      enabled: apiDonationForm.autoRenewalEmailEnabled ?? false,
      delayDays: apiDonationForm.autoRenewalEmailDelayDays ?? 5,
      subject: autoRenewalText.subject || getDefaultDonationAutoRenewalSubject(locale),
      content: autoRenewalText.body || getDefaultDonationAutoRenewalBody(orgName, userName, locale, t),
    },
    teamActivityNotificationsEnabled: apiDonationForm.teamActivityNotificationsEnabled ?? true,
  };

  const form = useForm<DonationAutomatedCommunicationsFormData>({ defaultValues });
  const { errors } = form.formState;
  const autoRenewalDelayDays = useWatch({ control: form.control, name: "autoRenewal.delayDays" });
  const autoRenewalEnabled = useWatch({ control: form.control, name: "autoRenewal.enabled" });
  const teamActivityNotificationsEnabled = useWatch({
    control: form.control,
    name: "teamActivityNotificationsEnabled",
  });

  const [openCard, setOpenCard] = useState<AutomatedEmailOpenedCard | null>(isCollapsible ? "thank-you-email" : null);

  const syncEmailInPreview = (next: AutomatedEmailOpenedCard | null) => {
    if (!liveFormData?.automatedCommunicationsData) {
      return;
    }
    const nextPreview: AutomatedEmailOpenedCard = next ?? "thank-you-email";
    if (liveFormData.automatedCommunicationsData.emailInPreview === nextPreview) {
      return;
    }
    setLiveFormData({
      ...liveFormData,
      automatedCommunicationsData: { ...liveFormData.automatedCommunicationsData, emailInPreview: nextPreview },
    });
  };

  const toggleOpen = (card: AutomatedEmailOpenedCard) =>
    setOpenCard((cur) => {
      const next = cur === card ? null : card;
      syncEmailInPreview(next);
      return next;
    });

  const openCardOnError = (card: AutomatedEmailOpenedCard) => {
    setOpenCard(card);
    syncEmailInPreview(card);
  };

  const handleInvalidSubmit = (errors: FieldErrors<DonationAutomatedCommunicationsFormData>) => {
    if (errors.thankYou) {
      return openCardOnError("thank-you-email");
    }
    if (errors.fundraiserThankYou) {
      return openCardOnError("thank-you-email-fundraiser");
    }
    if (errors.autoRenewal) {
      return openCardOnError("auto-renewal");
    }
  };

  useApplyThankYouAIDefaults(form, "thank-you-email");

  const { mutateAsync: updateDonationAutomatedEmails } = trpc.updateDonationAutomatedEmails.useMutation();
  const utils = trpc.useUtils();

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<DonationAutomatedCommunicationsFormData>({
    type: "donation-form",
    form,
    onInvalid: handleInvalidSubmit,
    saveData: async (formData) => {
      const result = await updateDonationAutomatedEmails({
        donationFormId: apiDonationForm.id,
        fundraiserToken: optionalFundraiserToken,
        fundraiserThankYouContent: showFundraiserCard ? formData.fundraiserThankYou.content : undefined,
        teamActivityNotificationsEnabled: showTeamActivityNotificationsCard
          ? formData.teamActivityNotificationsEnabled
          : undefined,
        thankYou: {
          subject: formData.thankYou.subject || "",
          content: formData.thankYou.content || "",
          attachmentUrl: formData.thankYou.attachment?.fileUrl ?? null,
        },
        autoRenewal: showAutoRenewal
          ? {
              enabled: formData.autoRenewal.enabled ?? false,
              delayDays: formData.autoRenewal.delayDays ?? 5,
              subject: formData.autoRenewal.subject || "",
              content: formData.autoRenewal.content || "",
            }
          : undefined,
      });
      await utils.getDonationAutomatedEmailContents.invalidate({ donationFormId: apiDonationForm.id });
      return result;
    },
    errorMessage: t("dashboard", "ticketing.error.updateThankYouEmail"),
  });

  useUpdateDonationDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      liveDataToMutate.automatedCommunicationsData.thankYou = {
        subject: formData.thankYou.subject ?? t("common", "thankYouForYourSupport"),
        body: formData.thankYou.content ?? t("dashboard", "editor.eventThankYouEmail.placeholder"),
      };
      const donationField = liveDataToMutate.donation.donationFormFields?.[0];
      if (donationField && showFundraiserCard) {
        donationField.fundraiserEmailBody =
          formData.fundraiserThankYou.content ?? t("dashboard", "editor.eventThankYouEmail.placeholder");
      }

      if (showAutoRenewal) {
        liveDataToMutate.automatedCommunicationsData.autoRenewal = {
          subject: formData.autoRenewal.subject || "",
          body: formData.autoRenewal.content || "",
        };
      }
    },
  });

  const { stepTitle, stepDescription } = getStepWording(t);

  return (
    <FormProvider {...form}>
      <FormStepContainer
        title={stepTitle}
        description={stepDescription}
        actionsProps={{ onBack: handleSaveAndBack, onNext: handleSaveAndNext }}
        form={form}
      >
        <Stack gap={2}>
          {shouldShowCustomMessagesDisabledBanner && <CustomMessagesDisabledBanner />}

          <AutomatedCommunicationCard<DonationAutomatedCommunicationsFormData>
            label={t(
              "dashboard",
              showFundraiserCard
                ? "ticketing.setThankYouEmail.thankYouEmailForDonorsLabel"
                : "ticketing.setThankYouEmail.thankYouEmailLabel"
            )}
            control={form.control}
            errors={errors}
            dataEmailType="thank-you-email"
            collapse={
              isCollapsible
                ? { isOpen: openCard === "thank-you-email", onToggle: () => toggleOpen("thank-you-email") }
                : undefined
            }
            toggleTooltip={t("dashboard", "ticketing.setThankYouEmail.alwaysEnabledTooltip")}
            fields={{ subject: "thankYou.subject", content: "thankYou.content" }}
            enabled
            lockedOn
            chip={{
              label: t("dashboard", "ticketing.setThankYouEmail.chipLabel.sentFollowingPurchase"),
              tooltip: t("dashboard", "ticketing.setThankYouEmail.chipLabel.sentFollowingPurchaseTooltip"),
              showWhen: "enabled",
            }}
            body={{
              attachmentFieldName: "thankYou.attachment",
              defaultValue: defaultValues.thankYou.content,
              customConfig: { date: false, location: hasLocation },
            }}
            dataTestPrefix="live-form-editor-customization-email"
            footnote={
              <Box marginTop={2} display="flex" justifyContent="flex-start">
                <SendAutomatedEmailTestButton<DonationAutomatedCommunicationsFormData>
                  emailType="donation-thank-you"
                  subjectFieldName="thankYou.subject"
                  contentFieldName="thankYou.content"
                  attachmentFieldName="thankYou.attachment"
                />
              </Box>
            }
          />

          {showFundraiserCard && (
            <AutomatedCommunicationCard<DonationAutomatedCommunicationsFormData>
              label={t("dashboard", "ticketing.setThankYouEmail.thankYouEmailForFundraisersLabel")}
              control={form.control}
              errors={errors}
              dataEmailType="thank-you-email-fundraiser"
              collapse={{
                isOpen: openCard === "thank-you-email-fundraiser",
                onToggle: () => toggleOpen("thank-you-email-fundraiser"),
              }}
              toggleTooltip={t("dashboard", "ticketing.setThankYouEmail.alwaysEnabledTooltip")}
              fields={{ content: "fundraiserThankYou.content" }}
              enabled
              lockedOn
              chip={{
                label: t("dashboard", "ticketing.setThankYouEmail.chipLabel.sentToFundraiser"),
                showWhen: "enabled",
              }}
              body={{
                defaultValue: localizedField?.fundraiserEmailBody || "",
                customConfig: { date: false, location: hasLocation },
              }}
              dataTestPrefix="live-form-editor-fundraiser-email"
              footnote={
                <Box marginTop={2} display="flex" justifyContent="flex-start">
                  <SendAutomatedEmailTestButton<DonationAutomatedCommunicationsFormData>
                    emailType="fundraiser-thank-you"
                    contentFieldName="fundraiserThankYou.content"
                  />
                </Box>
              }
            />
          )}

          {showAutoRenewal && (
            <AutomatedCommunicationCard<DonationAutomatedCommunicationsFormData>
              label={t("dashboard", "donation.autoRenewalReminder.label")}
              control={form.control}
              errors={errors}
              dataEmailType="auto-renewal"
              collapse={{ isOpen: openCard === "auto-renewal", onToggle: () => toggleOpen("auto-renewal") }}
              fields={{ subject: "autoRenewal.subject", content: "autoRenewal.content" }}
              enabled={Boolean(autoRenewalEnabled)}
              onEnabledChange={(next) => form.setValue("autoRenewal.enabled", next, { shouldDirty: true })}
              chip={{
                label: t("dashboard", "membership.renewalEmail.chipLabel.daysBefore", {
                  count: autoRenewalDelayDays ?? 0,
                }),
                showWhen: "enabled",
              }}
              offset={{
                name: "autoRenewal.delayDays",
                suffixLabel: t("dashboard", "membership.renewalEmail.daysBeforeRenewal", {
                  count: autoRenewalDelayDays ?? 0,
                }),
              }}
              body={{ defaultValue: defaultValues.autoRenewal.content, extraVariables: autoRenewalVariables }}
              dataTestPrefix="live-form-editor-auto-renewal"
              footnote={
                <Box marginTop={2} display="flex" justifyContent="flex-start">
                  <SendAutomatedEmailTestButton<DonationAutomatedCommunicationsFormData>
                    emailType="donation-renewal"
                    subjectFieldName="autoRenewal.subject"
                    contentFieldName="autoRenewal.content"
                  />
                </Box>
              }
            />
          )}

          {showTeamActivityNotificationsCard && (
            <TeamActivityNotificationsCard
              enabled={Boolean(teamActivityNotificationsEnabled)}
              onEnabledChange={(next) => form.setValue("teamActivityNotificationsEnabled", next, { shouldDirty: true })}
            />
          )}
        </Stack>
      </FormStepContainer>
    </FormProvider>
  );
};

const getStepWording = (t: Translate): { stepTitle: string; stepDescription: string } => ({
  stepTitle: t("dashboard", "ticketing.automatedCommunications.title"),
  stepDescription: t("dashboard", "ticketing.automatedCommunications.description"),
});

const chip = (variable: string, label: string) =>
  `<span data-custom-variable="{{${variable}}}" class="fr-deletable styled-tag-chip" contenteditable="false">${label}</span>`;

const getDefaultDonationAutoRenewalSubject = (locale: Locales): string => {
  return LocaleContent.DonationAutoRenewalSubject[normalizeContentLocale(locale)];
};

const getDefaultDonationAutoRenewalBody = (
  orgName: string,
  userName: string,
  locale: Locales,
  t: Translate
): string => {
  const signature = userName ? `<p>${userName}<br/>${orgName}</p>` : `<p>${orgName}</p>`;
  const firstName = chip("firstname", t("common", "firstName"));
  const renewalDate = chip("Renewal date", t("dashboard", "ticketing.renewalDate"));
  const renewalAmount = chip("Renewal amount", t("dashboard", "membership.renewalEmail.variable.renewalAmount"));
  const paymentMethod = chip("Payment method", t("dashboard", "membership.renewalEmail.variable.paymentMethod"));

  return LocaleContent.DonationAutoRenewalBody[normalizeContentLocale(locale)]({
    orgName,
    firstName,
    renewalDate,
    renewalAmount,
    paymentMethod,
    signature,
  });
};
